import { Injectable } from "@angular/core";
import { Action } from "@ngrx/store";
import { ActionWithPayload, SYNC_SUFFIX } from "@obg/ngrx-utils";

import { AppError } from "../app-error/app-error.model";

import { LoginWithAuthenticationToken } from "./auth.model";
import {
	CustomerNotVerifiedData,
	LoginRequest,
	LoginSuccessfulPayload,
	LoginSuccessfulResponse,
	LoginWithPersonalNumberRequest,
	LoginWithTokenRequest,
	LoginWithVerificationCodeRequest,
	LogoutPayload,
	SessionResponse,
	TimeToLiveResponse,
	TwoFactorAuthLogin,
	TwoFactorAuthLoginFailPayload,
	TwoFactorAuthLoginSuccessPayload,
} from "./session.model";

export const ACTION_PREFIX = "[Session]";

/**
 * WARNING!! TRY TO AVOID USING THIS CONST OUTSIDE OF `ngx.core/auth` FOLDER MUCH AS POSSIBLE!!
 * ACCESS TO `SESSION_ACTION_TYPE` WILL NOT BE ANY MORE ALLOWED SOON.
 * If you need to use this const outside of `ngx.core/auth` folder, please first contact core team.
 * What is this: This is actions required to maintain current session token.
 * All the application logic will be refactored soon not to use `SESSION_ACTION_TYPE` directly
 */
export const SESSION_ACTION_TYPE = {
	login: `${ACTION_PREFIX} Login`,
	loginSuccess: `${ACTION_PREFIX} Login Success`,
	loginFail: `${ACTION_PREFIX} Login Fail`,

	loginWithTwoFactorAuth: `${ACTION_PREFIX} Login With Two Factor Authentication`,
	loginWithTwoFactorAuthSuccess: `${ACTION_PREFIX} Login With Two Factor Authentication Success`,
	loginWithTwoFactorAuthFail: `${ACTION_PREFIX} Login With Two Factor Authentication Fail`,

	loginWithVerificationCode: `${ACTION_PREFIX} Login With Verification Code`,
	loginWithVerificationCodeSuccess: `${ACTION_PREFIX} Login With Verification Code Success`,
	loginWithVerificationCodeFail: `${ACTION_PREFIX} Login With Verification Code Fail`,

	loginWithPersonalNumber: `${ACTION_PREFIX} Login With Personal Number`,
	loginWithPersonalNumberSuccess: `${ACTION_PREFIX} Login With Personal Number Success`,
	loginWithPersonalNumberFail: `${ACTION_PREFIX} Login With Personal Number Fail`,

	loginWithToken: `${ACTION_PREFIX} Login With Token`,
	loginWithTokenSuccess: `${ACTION_PREFIX} Login With Token Success`,
	loginWithTokenFail: `${ACTION_PREFIX} Login With Token Fail`,

	loginWithAuthenticationToken: `${ACTION_PREFIX} Login With Authentication Token`,
	postRegistrationLoginWithAuthenticationToken: `${ACTION_PREFIX} Post Registration Login With Authentication Token`,
	loginWithAuthenticationTokenSuccess: `${ACTION_PREFIX} Login With Authentication Token Success`,
	loginWithAuthenticationTokenFail: `${ACTION_PREFIX} Login With Authentication Token Fail`,
	loginWithAuthenticationTokenSave: `${ACTION_PREFIX} Login With Authentication Token Save`,

	authenticationTransaction: `${ACTION_PREFIX} Authentication Transaction`,

	loginWithCookieSuccess: `${ACTION_PREFIX} Login With Cookie Success ${SYNC_SUFFIX}`,

	loginBrandRedirection: `${ACTION_PREFIX} Login Brand Redirection`,

	loginCountryRestriction: `${ACTION_PREFIX} Login Country Restriction`,

	updateCustomerNotVerifiedData: `${ACTION_PREFIX} Update Customer Not Verified Data ${SYNC_SUFFIX}`,
	clearCustomerNotVerifiedData: `${ACTION_PREFIX} Clear Customer Not Verified Data ${SYNC_SUFFIX}`,

	clearTermsAndConditionsToken: `${ACTION_PREFIX} Clear Terms And Conditions Token ${SYNC_SUFFIX}`,

	logout: `${ACTION_PREFIX} Logout`,
	logoutSuccess: `${ACTION_PREFIX} Logout Success`,
	/**
	 * Hardly possible, treated as logged out in frontend
	 */
	logoutFail: `${ACTION_PREFIX} Logout Fail`,

	/**
	 * Checks if current session is still active.
	 * Used on app start (may be used on other occasions too).
	 */
	verify: `${ACTION_PREFIX} Verify`,
	verifySuccess: `${ACTION_PREFIX} Verify Success`,
	verifyFail: `${ACTION_PREFIX} Verify Fail`,

	checkSessionExpired: `${ACTION_PREFIX} Check Session Expired ${SYNC_SUFFIX}`,
	sessionExpired: `${ACTION_PREFIX} Session Expired ${SYNC_SUFFIX}`,
	sessionInvalid: `${ACTION_PREFIX} Session Invalid ${SYNC_SUFFIX}`,
	resetSession: `${ACTION_PREFIX} Reset Session ${SYNC_SUFFIX}`,

	keepAlive: `${ACTION_PREFIX} Keep Alive`,
	keepAliveSuccess: `${ACTION_PREFIX} Keep Alive Success`,
	keepAliveFail: `${ACTION_PREFIX} Keep Alive Fail`,

	getNextTimeLogin: `${ACTION_PREFIX} Gets next time login`,
	getNextTimeLoginSuccess: `${ACTION_PREFIX} Gets next time login Success`,
	getNextTimeLoginFail: `${ACTION_PREFIX} Gets next time login Fail`,
};

/**
 * WARNING!! TRY TO AVOID USING THESE ACTIONS OUTSIDE OF `ngx.core/auth` FOLDER MUCH AS POSSIBLE!!
 * ACCESS TO `SessionAction` WILL NOT BE ANY MORE ALLOWED SOON.
 * If you need to use this outside of `ngx.core/auth` folder, please first contact core team.
 * What is this: This is actions required to maintain current session token.
 * All the application logic will be refactored soon not to use `SessionAction` directly
 */
@Injectable({
	providedIn: "root",
})
export class SessionAction {
	login(payload: LoginRequest): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.login,
			payload,
		};
	}

	loginSuccess(payload: LoginSuccessfulPayload): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginSuccess,
			payload,
		};
	}

	loginFail(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginFail,
			payload,
		};
	}

	loginWithTwoFactorAuthentication(payload: TwoFactorAuthLogin): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithTwoFactorAuth,
			payload,
		};
	}

	loginWithTwoFactorAuthenticationSuccess(payload: TwoFactorAuthLoginSuccessPayload): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithTwoFactorAuthSuccess,
			payload,
		};
	}

	loginWithTwoFactorAuthenticationFail(payload: TwoFactorAuthLoginFailPayload): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithTwoFactorAuthFail,
			payload,
		};
	}

	loginWithVerificationCode(payload: LoginWithVerificationCodeRequest): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithVerificationCode,
			payload,
		};
	}

	loginWithVerificationCodeSuccess(payload: LoginSuccessfulPayload): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
			payload,
		};
	}

	loginWithVerificationCodeFail(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithVerificationCodeFail,
			payload,
		};
	}

	loginWithAuthenticationToken(payload: any): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithAuthenticationToken,
			payload,
		};
	}

	postRegistrationLoginWithAuthToken(payload: LoginWithAuthenticationToken): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.postRegistrationLoginWithAuthenticationToken,
			payload,
		};
	}

	loginWithAuthenticationTokenSuccess(payload: any): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithAuthenticationTokenSuccess,
			payload,
		};
	}

	authenticationTransaction(payload: any): ActionWithPayload {
		return { type: SESSION_ACTION_TYPE.authenticationTransaction, payload };
	}

	loginWithAuthenticationTokenFail(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithAuthenticationTokenFail,
			payload,
		};
	}

	loginWithAuthenticationTokenSave(payload: any): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithAuthenticationTokenSave,
			payload,
		};
	}

	loginWithPersonalNumber(payload: LoginWithPersonalNumberRequest): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithPersonalNumber,
			payload,
		};
	}

	loginWithPersonalNumberSuccess(payload: LoginSuccessfulPayload): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
			payload,
		};
	}

	loginWithPersonalNumberFail(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithPersonalNumberFail,
			payload,
		};
	}

	loginWithToken(payload: LoginWithTokenRequest): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithToken,
			payload,
		};
	}

	loginWithTokenSuccess(payload: LoginSuccessfulResponse): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithTokenSuccess,
			payload,
		};
	}

	loginWithTokenFail(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginWithTokenFail,
			payload,
		};
	}

	loginWithCookieSuccess(): Action {
		return {
			type: SESSION_ACTION_TYPE.loginWithCookieSuccess,
		};
	}

	loginBrandRedirection(): Action {
		return {
			type: SESSION_ACTION_TYPE.loginBrandRedirection,
		};
	}

	loginCountryRestriction(countryCode: string): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.loginCountryRestriction,
			payload: countryCode,
		};
	}

	updateCustomerNotVerifiedData(payload: CustomerNotVerifiedData): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.updateCustomerNotVerifiedData,
			payload,
		};
	}

	clearCustomerNotVerifiedData(): Action {
		return {
			type: SESSION_ACTION_TYPE.clearCustomerNotVerifiedData,
		};
	}

	clearTermsAndConditionsToken(): Action {
		return {
			type: SESSION_ACTION_TYPE.clearTermsAndConditionsToken,
		};
	}

	logout(payload?: LogoutPayload): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.logout,
			payload,
		};
	}

	logoutSuccess(payload?: LogoutPayload): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.logoutSuccess,
			payload,
		};
	}

	logoutFail(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.logoutFail,
			payload,
		};
	}

	verify(newSessionToken?: string): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.verify,
			payload: newSessionToken,
		};
	}

	verifySuccess(payload: SessionResponse): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.verifySuccess,
			payload,
		};
	}

	verifyFail(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.verifyFail,
			payload,
		};
	}

	checkSessionExpired(): Action {
		return {
			type: SESSION_ACTION_TYPE.checkSessionExpired,
		};
	}

	sessionExpired(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.sessionExpired,
			payload,
		};
	}

	sessionInvalid(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.sessionInvalid,
			payload,
		};
	}

	resetSession(): Action {
		return {
			type: SESSION_ACTION_TYPE.resetSession,
		};
	}

	keepAlive(): Action {
		return {
			type: SESSION_ACTION_TYPE.keepAlive,
		};
	}

	keepAliveSuccess(payload: TimeToLiveResponse): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.keepAliveSuccess,
			payload,
		};
	}

	keepAliveFail(payload: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.keepAliveFail,
			payload,
		};
	}

	getNextTimeLogin(): Action {
		return {
			type: SESSION_ACTION_TYPE.getNextTimeLogin,
		};
	}

	getNextTimeLoginSuccess(customerCanLoginTime: string | null): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.getNextTimeLoginSuccess,
			payload: customerCanLoginTime,
		};
	}

	getNextTimeLoginFail(error: AppError): ActionWithPayload {
		return {
			type: SESSION_ACTION_TYPE.getNextTimeLoginFail,
			payload: error,
		};
	}
}
